import _extends from "@babel/runtime/helpers/extends";
import { LoadingOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Form, Input, App, Result, Spin } from "antd";
import { observer } from "mobx-react";
import { useEffect, useState, useCallback } from "react";
import { useAccountMagicLinkToken, useRealCommerceErrorHandling, useUserConfirmationCode } from "../../hooks";
import { IMPREZA_BUTTON_PROPS, localStorageTest, _i, __ } from "../../utils";
import { RealCommerceError } from "../../exceptions";
import { useStores } from "../../store";
import { providedModal } from "../../others";

/**
 * Old local storage name.
 *
 * @deprecated
 */
const LOCAL_STORAGE_JWT_TOKEN_NAME_DEPRECATED = "realCommerceAccountLoginJwt";
const LOCAL_STORAGE_JWT_TOKEN_NAME = "real-commerce-account-jwt";

/**
 * This form automatically handles JWT token creation, and setting to main store.
 */
const AccountMagicLinkForm = observer(() => {
  const {
    message,
    modal
  } = App.useApp();
  const {
    accountStore
  } = useStores();
  const [busy, setBusy] = useState(false);
  const [createdForEmail, setCreatedForEmail] = useState();
  const initialValues = {
    email: ""
  };
  const [form] = Form.useForm();
  const handleRealCommerceError = useRealCommerceErrorHandling();
  const onFinish = useCallback(async _ref => {
    let {
      email
    } = _ref;
    setBusy(true);
    try {
      await accountStore.sendMagicLink(email);
      setCreatedForEmail(email);
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
    } finally {
      setBusy(false);
    }
  }, []);

  // Check if currently a JWT is set or listen to magic link tokens in URL
  const jwtFromDeprecatedLocalStorage = localStorageTest() && localStorage.getItem(LOCAL_STORAGE_JWT_TOKEN_NAME_DEPRECATED);
  if (jwtFromDeprecatedLocalStorage) {
    localStorage.setItem(LOCAL_STORAGE_JWT_TOKEN_NAME, jwtFromDeprecatedLocalStorage);
    localStorage.removeItem(LOCAL_STORAGE_JWT_TOKEN_NAME_DEPRECATED);
  }
  const {
    magicLinkToken,
    jwt,
    erase,
    saveJwt
  } = useAccountMagicLinkToken(async magicLinkToken => {
    try {
      return await accountStore.receiveJwt(magicLinkToken);
    } catch (e) {
      if (e instanceof RealCommerceError) {
        providedModal("error", /*#__PURE__*/React.createElement(Result, {
          title: e.message,
          status: "500",
          style: {
            paddingBottom: 15
          }
        }), modal);
      }
      throw e;
    }
  }, LOCAL_STORAGE_JWT_TOKEN_NAME);

  // Double-opt-in for envato purchases and affiliate user sign-up -> automatically login
  const {
    busy: doubleOptInBusy,
    result: doubleOptInResult
  } = useUserConfirmationCode();
  useEffect(() => {
    if (doubleOptInResult) {
      saveJwt(doubleOptInResult.jwt);
      message.success(__("Successfully activated your account. We have logged you in automatically!"), 10);
    }
  }, [doubleOptInResult, saveJwt]);

  // Automatically fetch user when JWT already known
  useEffect(() => {
    if (jwt) {
      accountStore.createSessionFromJwt(jwt).catch(e => {
        if (e instanceof RealCommerceError) {
          var _e$original;
          providedModal("error", /*#__PURE__*/React.createElement(Result, {
            title: e.message,
            status: "error",
            style: {
              paddingBottom: 15
            }
          }), modal);
          if (((_e$original = e.original) === null || _e$original === void 0 ? void 0 : _e$original.status) === 401) {
            // Reset currently known JWT so the magic link form is shown again
            erase();
          }
        }
      });
    }
  }, [jwt]);
  return magicLinkToken || jwt ? /*#__PURE__*/React.createElement(Result, {
    title: __("Login to your account..."),
    icon: /*#__PURE__*/React.createElement(LoadingOutlined, null)
  }) : /*#__PURE__*/React.createElement("div", {
    className: "real-commerce-magic-link-form"
  }, /*#__PURE__*/React.createElement(Spin, {
    spinning: busy || doubleOptInBusy
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: 25,
      fontWeight: "bold"
    }
  }, __("Sign-in to your account")), /*#__PURE__*/React.createElement("p", null, __("Sign in and manage your licenses, subscriptions and invoices. To sign up, simply request a magic link that takes you directly to the customer center.")), createdForEmail ? /*#__PURE__*/React.createElement(Result, {
    title: __("Email successfully sent!"),
    subTitle: _i(__("We have sent a magic link to {{strong}}%s{{/strong}}. Please check your inbox! You can close this page.", createdForEmail), {
      strong: /*#__PURE__*/React.createElement("strong", null)
    }),
    status: "success"
  }) : /*#__PURE__*/React.createElement(Form, {
    form: form,
    onFinish: onFinish,
    labelCol: {
      span: 0
    },
    initialValues: initialValues,
    labelWrap: true
  }, /*#__PURE__*/React.createElement(Form.Item, {
    name: "email",
    rules: [{
      type: "email",
      required: true,
      message: __("Please enter a valid email address!")
    }]
  }, /*#__PURE__*/React.createElement(Input, {
    size: "large",
    style: {
      maxWidth: 500
    },
    prefix: /*#__PURE__*/React.createElement(MailOutlined, {
      style: {
        opacity: 0.5,
        marginRight: 15
      }
    }),
    placeholder: __("Your email address...")
  })), /*#__PURE__*/React.createElement(Form.Item, null, /*#__PURE__*/React.createElement(Button, _extends({
    type: "primary",
    size: "large",
    htmlType: "submit"
  }, IMPREZA_BUTTON_PROPS), __("Send magic link")), /*#__PURE__*/React.createElement("a", {
    href: "#/envato-purchase",
    style: {
      display: "block",
      marginTop: 10
    }
  }, __("Did you buy at codecanyon.net? Create an account!")), /*#__PURE__*/React.createElement("a", {
    href: "#/create-affiliate",
    style: {
      display: "block",
      marginTop: 10
    }
  }, __("Do you want to promote our products? Sign up as an affiliate!"))))));
});
export { AccountMagicLinkForm, LOCAL_STORAGE_JWT_TOKEN_NAME };